import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { DOCS, findDoc, getDoc, type DocEntry } from "../../lib/docs/loader";
import { checkedIdsSync, subscribeToDocChecks } from "../../lib/docs/checkbox-state";
import MarkdownRenderer from "./MarkdownRenderer";

// Full docs experience. Two-column layout — TOC on the left grouped by
// top-level folder (root / architecture / spec / spec/modules / stack /
// setup / archive), doc content on the right. Wiki-links inside docs
// navigate within the viewer without leaving the tab.

export default function DocsScreen() {
  const [location, setLocation] = useLocation();
  const selectedPath = pathFromLocation(location);
  const [checkboxTick, setCheckboxTick] = useState(0);
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => groupDocs(DOCS), []);
  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return groups;
    const needle = filter.toLowerCase();
    return groups
      .map((g) => ({ ...g, docs: g.docs.filter((d) => matchesFilter(d, needle)) }))
      .filter((g) => g.docs.length > 0);
  }, [groups, filter]);

  const doc = getDoc(selectedPath);

  function selectPath(path: string) {
    setLocation(`/docs/${path}`);
  }

  function navigate(target: string) {
    const resolved = findDoc(target);
    if (resolved) selectPath(resolved.path);
    else console.warn(`Doc not found: ${target}`);
  }

  return (
    <div className="flex h-full min-h-0">
      {/* TOC */}
      <aside className="w-72 flex-none border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 overflow-y-auto">
        <div className="p-3 border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-slate-50 dark:bg-slate-900/40">
          <input
            type="search"
            placeholder="Filter docs…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <nav className="p-2 space-y-4">
          {filteredGroups.map((g) => (
            <div key={g.label}>
              <p className="px-2 mb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                {g.label}
              </p>
              <ul>
                {g.docs.map((d) => (
                  <li key={d.path}>
                    <button
                      onClick={() => selectPath(d.path)}
                      className={`w-full text-left px-2 py-1 rounded text-xs truncate ${
                        d.path === selectedPath
                          ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 font-medium"
                          : "hover:bg-slate-200/60 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                      }`}
                      title={d.path}
                    >
                      {d.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-y-auto bg-white dark:bg-[#131825]">
        {doc ? (
          <div className="max-w-3xl mx-auto p-6">
            <DocHeader doc={doc} tick={checkboxTick} />
            <MarkdownRenderer
              content={doc.content}
              docPath={doc.path}
              onNavigate={navigate}
              onCheckboxChange={() => setCheckboxTick((t) => t + 1)}
            />
          </div>
        ) : (
          <div className="p-6 text-sm text-slate-500">Document not found: {selectedPath}</div>
        )}
      </main>
    </div>
  );
}

function DocHeader({ doc, tick: _tick }: { doc: DocEntry; tick: number }) {
  // Subscribe to this doc's shared check state so the progress bar
  // refreshes on the initial fetch and on any toggle originating in the
  // renderer. Local `_tick` is still honoured for the same-render
  // updates the renderer already fires via onCheckboxChange.
  const [subTick, setSubTick] = useState(0);
  useEffect(() => {
    const unsub = subscribeToDocChecks(doc.path, () => {
      setSubTick((t) => t + 1);
    });
    return unsub;
  }, [doc.path]);

  const { total, done } = useMemo(() => taskProgress(doc), [doc, _tick, subTick]);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="mb-4 pb-3 border-b border-slate-200 dark:border-slate-800">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{doc.path}</p>
      {total > 0 && (
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-slate-500 whitespace-nowrap tabular-nums">
            {done} / {total} done
          </span>
        </div>
      )}
    </div>
  );
}

function taskProgress(doc: DocEntry): { total: number; done: number } {
  const tasks = extractTaskTexts(doc.content);
  if (tasks.length === 0) return { total: 0, done: 0 };
  const ticked = checkedIdsSync(doc.path);
  const done = tasks.filter((t) => ticked.has(slug(t))).length;
  return { total: tasks.length, done };
}

function extractTaskTexts(md: string): string[] {
  const out: string[] = [];
  const re = /^\s*[-*+]\s+\[[ xX]\]\s+(.+)$/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md)) !== null) {
    out.push(m[1]!);
  }
  return out;
}

function slug(text: string): string {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").slice(0, 80);
}

interface DocGroup {
  label: string;
  docs: DocEntry[];
}

function groupDocs(all: DocEntry[]): DocGroup[] {
  const groups: Record<string, DocEntry[]> = {
    Root: [],
    Architecture: [],
    Spec: [],
    "Modules": [],
    Stack: [],
    Setup: [],
    Archive: [],
  };
  for (const d of all) {
    if (d.path.startsWith("archive/")) groups.Archive.push(d);
    else if (d.path.startsWith("architecture/")) groups.Architecture.push(d);
    else if (d.path.startsWith("spec/modules/")) groups.Modules.push(d);
    else if (d.path.startsWith("spec/")) groups.Spec.push(d);
    else if (d.path.startsWith("stack/")) groups.Stack.push(d);
    else if (d.path.startsWith("setup/")) groups.Setup.push(d);
    else groups.Root.push(d);
  }
  const order: (keyof typeof groups)[] = ["Root", "Architecture", "Spec", "Modules", "Stack", "Setup", "Archive"];
  return order.map((label) => ({ label, docs: groups[label] })).filter((g) => g.docs.length > 0);
}

function matchesFilter(d: DocEntry, needle: string): boolean {
  if (d.path.toLowerCase().includes(needle)) return true;
  if (d.title.toLowerCase().includes(needle)) return true;
  if (d.content.toLowerCase().includes(needle)) return true;
  return false;
}

function defaultDoc(): string {
  return findDoc("README")?.path ?? DOCS[0]?.path ?? "README.md";
}

function pathFromLocation(location: string): string {
  // `/docs` or `/docs/` → default; `/docs/<rest>` → rest as the doc path.
  const stripped = location.replace(/^\/docs\/?/, "");
  return stripped.length > 0 ? decodeURIComponent(stripped) : defaultDoc();
}
